// Copyright (c) Mysten Labs, Inc.
// Modifications Copyright (c) 2024 IOTA Stiftung
// SPDX-License-Identifier: Apache-2.0

import { wrapCreateBrowserRouter } from '@sentry/react';
import { createBrowserRouter, Navigate, useLocation, useParams } from 'react-router-dom';
import { AddressResultPage } from './address-result/AddressResult';
import { AbstractAccountResultPage } from './abstract-account-result/AbstractAccountResult';
import { CheckpointDetail } from './checkpoints/CheckpointDetail';
import { CookiePolicyPage } from './cookie-policy/CookiePolicyPage';
import { EpochDetail } from './epochs/EpochDetail';
import { Home } from './home/Home';
import { ObjectResult } from './object-result/ObjectResult';
import { Recent } from './recent';
import { TransactionResult } from './transaction-result/TransactionResult';
import { ValidatorDetails } from './validator/ValidatorDetails';
import { ValidatorPageResult } from './validators/Validators';
import { Layout } from '~/components';
import { IdentityResult } from './trust-framework/identity-result/IdentityResult';
import { NotarizationResult } from './trust-framework/notarization-result/NotarizationResult';

interface RedirectWithIdProps {
    base: string;
}

function RedirectWithId({ base }: RedirectWithIdProps): JSX.Element {
    const params = useParams();
    const { search } = useLocation();
    return <Navigate to={`/${base}/${params.id}${search}`} replace />;
}

const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
    {
        path: '/',
        element: <Layout />,
        children: [
            { path: '/', element: <Home /> },
            { path: 'cookie-policy', element: <CookiePolicyPage /> },
            { path: 'recent', element: <Recent /> },
            { path: 'object/:id', element: <ObjectResult /> },
            { path: 'checkpoint/:id', element: <CheckpointDetail /> },
            { path: 'epoch/current', element: <EpochDetail /> },
            { path: 'txblock/:id', element: <TransactionResult /> },
            { path: 'epoch/:id', element: <EpochDetail /> },
            { path: 'address/:id', element: <AddressResultPage /> },
            { path: 'account/:id', element: <AbstractAccountResultPage /> },
            { path: 'validators', element: <ValidatorPageResult /> },
            { path: 'validator/:id', element: <ValidatorDetails /> },
            { path: 'identity/:id', element: <IdentityResult /> },
            { path: 'notarization/:id', element: <NotarizationResult /> },
        ],
    },
    {
        path: '/transactions',
        element: <Navigate to="/recent" replace />,
    },
    // Support legacy routes:
    {
        path: '/objects/:id',
        element: <RedirectWithId base="object" />,
    },
    {
        path: '/transaction/:id',
        element: <RedirectWithId base="txblock" />,
    },
    {
        path: '/transactions/:id',
        element: <RedirectWithId base="txblock" />,
    },
    {
        path: '/addresses/:id',
        element: <RedirectWithId base="address" />,
    },
    // 404 route:
    { path: '*', element: <Navigate to="/" replace /> },
]);
